import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { toast } from 'react-hot-toast';
import {
  ApiResponse,
  AuthResponse,
  LoginCredentials,
  RegisterData,
  MFASetupResponse,
  User,
  Chat,
  Message,
  UserConversation,
  SaveConversationData
} from '../types';

class ApiService {
  // --- User Settings API ---
  async getUserSettings(): Promise<ApiResponse<{ settings: UserSettings }>> {
    const res = await this.get<{ settings: UserSettings }>('/api/users/settings');
    if (res && 'settings' in res) {
      return { success: res.success, data: { settings: (res as any).settings }, message: res.message ?? '' };
    }
    if (res && res.data && 'settings' in res.data) {
      return { ...res, message: res.message ?? '' };
    }
    return {
      success: false,
      data: {
        settings: {
          genAiMode: '',
          projects: [],
          llmHost: '',
          llmModel: '',
          llmApiKey: ''
        }
      },
      message: 'User settings not found.'
    };
  
  }
  async updateUserSettings(settings: UserSettings): Promise<ApiResponse<{ settings: UserSettings }>> {
    return this.put<{ settings: UserSettings }>('/api/users/settings', settings);
  }
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    // Always use the full URL from environment variable
    // This ensures consistent behavior in both development and production
    this.baseURL = process.env.REACT_APP_API_URL || 'https://spaarxsense-react.onrender.com';

    console.log('API Base URL:', this.baseURL); // Debug log

    this.api = axios.create({
      baseURL: this.baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        // Check for new token in response headers
        const newToken = response.headers['x-new-token'];
        if (newToken) {
          localStorage.setItem('token', newToken);
        }
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (refreshToken) {
              const response = await this.refreshToken(refreshToken);
              if (response.success && response.data) {
                const { token, refreshToken: newRefreshToken } = response.data;

                localStorage.setItem('token', token);
                localStorage.setItem('refreshToken', newRefreshToken);

                originalRequest.headers.Authorization = `Bearer ${token}`;
                return this.api(originalRequest);
              }
            }
          } catch (refreshError) {
            // Refresh failed, redirect to login
            this.handleAuthError();
            return Promise.reject(refreshError);
          }
        }

        // Handle other errors
        this.handleApiError(error);
        return Promise.reject(error);
      }
    );
  }

  private handleApiError(error: any) {
    if (error.response?.data?.message) {
      toast.error(error.response.data.message);
    } else if (error.message) {
      toast.error(error.message);
    } else {
      toast.error('An unexpected error occurred');
    }
  }

  private handleAuthError() {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    window.location.href = '/login';
  }

  // Generic API methods
  private async request<T>(config: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.api.request<ApiResponse<T>>(config);
    return response.data;
  }

  private async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'GET', url });
  }

  private async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'POST', url, data });
  }

  private async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'PUT', url, data });
  }

  private async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    return this.request<T>({ ...config, method: 'DELETE', url });
  }

  // Authentication API
  async register(data: RegisterData): Promise<AuthResponse> {
    const response = await this.post<AuthResponse['data']>('/auth/register', data);
    return response as AuthResponse;
  }

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      console.log('Login request to:', `${this.baseURL}/api/auth/login`);
      const response = await this.post<AuthResponse['data']>('/api/auth/login', credentials);
      console.log('Login response:', response);
      return response as AuthResponse;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }

  async verifyMFA(tempToken: string, mfaToken: string, rememberMe?: boolean): Promise<AuthResponse> {
    const response = await this.post<AuthResponse['data']>('/auth/verify-mfa', {
      tempToken,
      mfaToken,
      rememberMe
    });
    return response as AuthResponse;
  }

  async logout(): Promise<ApiResponse> {
    return this.post('/api/auth/logout');
  }

  async getCurrentUser(): Promise<ApiResponse<{ user: User }>> {
    return this.get<{ user: User }>('/api/auth/me');
  }

  async verifyEmail(token: string): Promise<ApiResponse> {
    return this.post('/auth/verify-email', { token });
  }

  async setupMFA(): Promise<MFASetupResponse> {
    const response = await this.post<MFASetupResponse['data']>('/auth/setup-mfa');
    return response as MFASetupResponse;
  }

  async verifyMFASetup(token: string): Promise<ApiResponse<{ backupCodes: string[] }>> {
    return this.post<{ backupCodes: string[] }>('/auth/verify-mfa-setup', { token });
  }

  async disableMFA(mfaToken: string): Promise<ApiResponse> {
    return this.post('/auth/disable-mfa', { mfaToken });
  }

  async refreshToken(refreshToken: string): Promise<ApiResponse<{ token: string; refreshToken: string }>> {
    return this.post<{ token: string; refreshToken: string }>('/auth/refresh-token', { refreshToken });
  }

  // User API
  async updateProfile(data: Partial<User>): Promise<ApiResponse<{ user: User }>> {
    return this.put<{ user: User }>('/users/profile', data);
  }

  async updatePreferences(preferences: Partial<User['preferences']>): Promise<ApiResponse<{ user: User }>> {
    return this.put<{ user: User }>('/users/preferences', preferences);
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<ApiResponse> {
    return this.put('/users/password', { currentPassword, newPassword });
  }

  async uploadAvatar(file: File): Promise<ApiResponse<{ avatarUrl: string }>> {
    const formData = new FormData();
    formData.append('avatar', file);

    return this.request<{ avatarUrl: string }>({
      method: 'POST',
      url: '/users/avatar',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  // Chat API
  async getChats(options?: {
    page?: number;
    limit?: number;
    status?: string;
    tags?: string[];
    isFavorite?: boolean;
  }): Promise<ApiResponse<{ chats: Chat[]; pagination: any }>> {
    const params = new URLSearchParams();
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.status) params.append('status', options.status);
    if (options?.tags) options.tags.forEach(tag => params.append('tags', tag));
    if (options?.isFavorite !== undefined) params.append('isFavorite', options.isFavorite.toString());

    const response = await this.get<{ chats: Chat[]; pagination: any }>(`/api/chats?${params.toString()}`);
    return response;
  }

  async getChat(chatId: string): Promise<ApiResponse<{ chat: Chat }>> {
    return this.get<{ chat: Chat }>(`/api/chats/${chatId}`);
  }

  async createChat(data: { title?: string; aiModel?: string; aiSettings?: any }): Promise<ApiResponse<{ chat: Chat }>> {
    return this.post<{ chat: Chat }>('/api/chats', data);
  }

  async updateChat(chatId: string, data: Partial<Chat>): Promise<ApiResponse<{ chat: Chat }>> {
    return this.put<{ chat: Chat }>(`/api/chats/${chatId}`, data);
  }

  async deleteChat(chatId: string): Promise<ApiResponse> {
    return this.delete(`/api/chats/${chatId}`);
  }

  async archiveChat(chatId: string): Promise<ApiResponse<{ chat: Chat }>> {
    return this.put<{ chat: Chat }>(`/api/chats/${chatId}/archive`);
  }

  async favoriteChat(chatId: string): Promise<ApiResponse<{ chat: Chat }>> {
    return this.put<{ chat: Chat }>(`/api/chats/${chatId}/favorite`);
  }

  async searchChats(query: string, options?: { limit?: number }): Promise<ApiResponse<{ chats: Chat[] }>> {
    const params = new URLSearchParams();
    params.append('q', query);
    if (options?.limit) params.append('limit', options.limit.toString());

    return this.get<{ chats: Chat[] }>(`/api/chats/search?${params.toString()}`);
  }

  // Message API
  async getMessages(chatId: string, options?: {
    page?: number;
    limit?: number;
    before?: string;
  }): Promise<ApiResponse<{ messages: Message[]; pagination: any }>> {
    const params = new URLSearchParams();
    if (options?.page) params.append('page', options.page.toString());
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.before) params.append('before', options.before);

    const response = await this.get<{ messages: Message[]; pagination: any }>(`/api/chats/${chatId}/messages?${params.toString()}`);
    return response;
  }

  async sendMessage(chatId: string, content: string, options?: {
    type?: string;
    parentMessage?: string;
  }): Promise<ApiResponse<{ message: Message }>> {
    return this.post<{ message: Message }>(`/api/chats/${chatId}/messages`, {
      content,
      ...options
    });
  }

  async saveAIResponse(chatId: string, content: string, options?: {
    tokensUsed?: number;
    responseTime?: number;
  }): Promise<ApiResponse<{ message: Message }>> {
    return this.post<{ message: Message }>(`/api/chats/${chatId}/messages/ai-response`, {
      content,
      tokensUsed: options?.tokensUsed || 50,
      responseTime: options?.responseTime || 1000
    });
  }

  async editMessage(chatId: string, messageId: string, content: string): Promise<ApiResponse<{ message: Message }>> {
    return this.put<{ message: Message }>(`/api/chats/${chatId}/messages/${messageId}`, { content });
  }

  async deleteMessage(chatId: string, messageId: string): Promise<ApiResponse> {
    return this.delete(`/api/chats/${chatId}/messages/${messageId}`);
  }

  async addReaction(chatId: string, messageId: string, emoji: string): Promise<ApiResponse<{ message: Message }>> {
    return this.post<{ message: Message }>(`/api/chats/${chatId}/messages/${messageId}/reactions`, { emoji });
  }

  async removeReaction(chatId: string, messageId: string): Promise<ApiResponse<{ message: Message }>> {
    return this.delete<{ message: Message }>(`/api/chats/${chatId}/messages/${messageId}/reactions`);
  }

  async searchMessages(chatId: string, query: string, options?: { limit?: number }): Promise<ApiResponse<{ messages: Message[] }>> {
    const params = new URLSearchParams();
    params.append('q', query);
    if (options?.limit) params.append('limit', options.limit.toString());

    return this.get<{ messages: Message[] }>(`/api/chats/${chatId}/messages/search?${params.toString()}`);
  }

  // MCP Retrieval chat endpoint
  async chatWithFastAI(payload: any): Promise<ApiResponse<any>> {
    try {
      console.log('Sending request to MCP Retrieval:', {
        url: '/api/fastai/query',
        payload: JSON.parse(JSON.stringify(payload, (key, value) =>
          key === 'api_key' ? '***' : value
        ))
      });

      const response = await this.api.post('/api/fastai/query', payload, {
        timeout: 120000, // 2 minutes timeout
        headers: {
          'Content-Type': 'application/json',
        },
        validateStatus: () => true, // Always resolve the promise
      });

      console.log('Received MCP Retrieval response:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data
      });

      // Handle non-200 status codes
      if (response.status !== 200) {
        const errorMessage = response.data?.message || 'Failed to get response from AI service';
        return {
          success: false,
          message: errorMessage,
          status: response.status,
          data: response.data
        };
      }

      return {
        success: true,
        message: 'Success',
        data: response.data,
        status: response.status
      };
    } catch (error: any) {
      console.error('Error in chatWithMCPRetrieval:', {
        message: error.message,
        code: error.code,
        stack: error.stack,
        response: error.response?.data || 'No response data'
      });

      return {
        success: false,
        message: error.response?.data?.message || 'Failed to communicate with MCP Retrieval service',
        error: error.message,
        status: error.response?.status || 500
      };
    }
  }

  // File upload
  async uploadFile(file: File, chatId?: string): Promise<ApiResponse<{ url: string; filename: string }>> {
    const formData = new FormData();
    formData.append('file', file);
    if (chatId) formData.append('chatId', chatId);

    return this.request<{ url: string; filename: string }>({
      method: 'POST',
      url: '/upload',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  // UserConversation API
  async getUserConversations(options?: {
    page?: number;
    limit?: number;
    status?: string;
    tags?: string[];
    isBookmarked?: boolean;
    search?: string;
  }): Promise<ApiResponse<{ conversations: UserConversation[]; pagination: any }>> {
    try {
      const params = new URLSearchParams();
      if (options?.page) params.append('page', options.page.toString());
      if (options?.limit) params.append('limit', options.limit.toString());
      if (options?.status) params.append('status', options.status);
      if (options?.tags) options.tags.forEach(tag => params.append('tags', tag));
      if (options?.isBookmarked !== undefined) params.append('isBookmarked', options.isBookmarked.toString());
      if (options?.search) params.append('search', options.search);

      console.log('Fetching user conversations with params:', params.toString());
      const response = await this.get<{ conversations: UserConversation[]; pagination: any }>(`/api/conversations?${params.toString()}`);
      console.log('User conversations response:', response);
      return response;
    } catch (error) {
      console.error('Error in getUserConversations:', error);
      throw error;
    }
  }

  async getUserConversation(conversationId: string): Promise<ApiResponse<{ conversation: UserConversation }>> {
    try {
      console.log(`Fetching conversation with ID: ${conversationId}`);
      const response = await this.get<{ conversation: UserConversation }>(`/api/conversations/${conversationId}`);
      console.log('Get conversation response:', response);
      return response;
    } catch (error) {
      console.error('Error in getUserConversation:', error);
      throw error;
    }
  }

  async createUserConversation(data: {
    chatId: string;
    title?: string;
    summary?: string;
  }): Promise<ApiResponse<UserConversation>> {
    try {
      console.log('Creating new conversation:', data);
      const response = await this.post<UserConversation>('/api/conversations', data);
      console.log('Create conversation response:', response);
      return response;
    } catch (error) {
      console.error('Error in createUserConversation:', error);
      throw error;
    }
  }

  async saveCurrentConversation(data: SaveConversationData): Promise<ApiResponse<UserConversation>> {
    try {
      console.log('Saving current conversation:', data);
      const response = await this.post<UserConversation>('/api/conversations/save-current', data);
      console.log('Save current conversation response:', response);
      return response;
    } catch (error) {
      console.error('Error in saveCurrentConversation:', error);
      throw error;
    }
  }

  async updateUserConversation(conversationId: string, data: Partial<UserConversation>): Promise<ApiResponse<UserConversation>> {
    try {
      console.log(`Updating conversation ${conversationId}:`, data);
      const response = await this.put<UserConversation>(`/api/conversations/${conversationId}`, data);
      console.log('Update conversation response:', response);
      return response;
    } catch (error) {
      console.error('Error in updateUserConversation:', error);
      throw error;
    }
  }

  async deleteUserConversation(conversationId: string): Promise<ApiResponse> {
    try {
      console.log(`Deleting conversation ${conversationId}`);
      const response = await this.delete(`/api/conversations/${conversationId}`);
      console.log('Delete conversation response:', response);
      return response;
    } catch (error) {
      console.error('Error in deleteUserConversation:', error);
      throw error;
    }
  }

  async getUserConversationStats(): Promise<ApiResponse<{
    totalConversations: number;
    totalMessages: number;
    averageMessagesPerConversation: number;
    bookmarkedConversations: number;
    totalTokens: number;
    conversationsByStatus: { [key: string]: number };
    conversationsByMonth: { month: string; count: number }[];
    topTags: { tag: string; count: number }[];
  }>> {
    try {
      console.log('Fetching user conversation stats');
      const response = await this.get<{
        totalConversations: number;
        totalMessages: number;
        averageMessagesPerConversation: number;
        bookmarkedConversations: number;
        totalTokens: number;
        conversationsByStatus: { [key: string]: number };
        conversationsByMonth: { month: string; count: number }[];
        topTags: { tag: string; count: number }[];
      }>('/api/conversations/stats');
      console.log('User conversation stats response:', response);
      return response;
    } catch (error) {
      console.error('Error in getUserConversationStats:', error);
      throw error;
    }
  }
}

export interface UserSettings {
  genAiMode: string;
  projects: string[];
  llmHost: string;
  llmModel: string;
  llmApiKey: string;
}

export const apiService = new ApiService();
export default apiService;
